import React from "react";
import { Dialog, Box, IconButton, Typography, Button, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { isPdfDocument } from "utils/kycDocument";

interface AdminDocumentViewerModalProps {
  open: boolean;
  onClose: () => void;
  imageSrc: string | null;
  objectKey?: string;
  title?: string;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onDownload?: () => void;
}

function AdminDocumentViewerModal({
  open,
  onClose,
  imageSrc,
  objectKey,
  title = "KYC document",
  loading = false,
  error = null,
  onRetry,
  onDownload,
}: AdminDocumentViewerModalProps) {
  const isPdf = objectKey ? isPdfDocument(objectKey) : false;

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box sx={{ position: "relative", minHeight: "100vh", bgcolor: "#111" }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ position: "absolute", top: 16, left: 16, right: 16, zIndex: 2 }}
        >
          <Typography variant="subtitle1" sx={{ color: "#fff", flex: 1 }}>
            {title}
          </Typography>
          {onDownload && !loading && !error && imageSrc && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={onDownload}
              sx={{ bgcolor: "#007CBF", "&:hover": { bgcolor: "#006aa3" } }}
            >
              Download
            </Button>
          )}
          <IconButton onClick={onClose} sx={{ color: "#fff" }} aria-label="Close preview">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
            pt: 10,
          }}
        >
          {loading && (
            <Typography color="#fff">Loading document...</Typography>
          )}
          {!loading && error && (
            <Box sx={{ textAlign: "center" }}>
              <Typography color="#fff" sx={{ mb: 2 }}>
                {error}
              </Typography>
              {onRetry && (
                <Button variant="contained" onClick={onRetry}>
                  Retry
                </Button>
              )}
            </Box>
          )}
          {!loading && !error && imageSrc && (
            isPdf ? (
              <Box
                component="iframe"
                src={imageSrc}
                title={title}
                sx={{
                  width: "100%",
                  maxWidth: 960,
                  height: "calc(100vh - 120px)",
                  border: 0,
                  bgcolor: "#fff",
                  borderRadius: 1,
                }}
              />
            ) : (
              <img
                src={imageSrc}
                alt={title}
                style={{ maxWidth: "100%", maxHeight: "calc(100vh - 120px)", objectFit: "contain" }}
              />
            )
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

export default AdminDocumentViewerModal;
